import { t } from "i18next";
//@ts-ignore
import Pagination from "react-js-pagination";
import TinyLoader from "../../../loaders/TinyLoader";
import SearchField from "../../../common/SearchField";
import UserProfileHoverCard from "../../../common/UserProfileHoverCard";
import { AnalyticsWidgetUiDetailsDto } from "../../../../types/analytics/AnalyticsWidgetUiDetailsDto";
import AnalyticsFilterInputs from "../../../../types/analytics/AnalyticsFilterInputs";
import { useMultiScoreTable } from "./useMultiScoreTable";
import { displayEmployeeDimensionLabel } from "../../../../helpers/analytics/displayEmployeeDimensionLabel";

interface MultiScoreTableMobileProps {
  widget: AnalyticsWidgetUiDetailsDto;
  analyticsPageId: number;
  filters: AnalyticsFilterInputs;
}

const MultiScoreTableMobile = ({
  widget,
  analyticsPageId,
  filters,
}: MultiScoreTableMobileProps) => {
  const {
    dataToRender,
    isLoading,
    noResultsLoaded,
    pageSize,
    totalItemCount,
    pageSearchDetails,
    handlePageChange,
    handleSearchChange,
    userContext,
  } = useMultiScoreTable({ widget, analyticsPageId, filters });

  const isSnapshot = (filters.pointInTimeDate?.trim() ?? "") !== "";

  if (!dataToRender) return null;

  return (
    <div className="w-full">
      <SearchField
        onSearch={handleSearchChange}
        isLoading={isLoading}
        loaderColor={userContext.user.client.accentHexColour}
      />

      {noResultsLoaded && (
        <em className="block text-center italic py-4">
          {t("Pages.Analytics.Common.NoTableData")}
        </em>
      )}

      {!noResultsLoaded && (
        <>
          <div className="space-y-4 mt-4">
            {dataToRender.map((employee) => (
              <div
                key={employee.id}
                className="bg-white rounded-lg shadow-sm border p-4 space-y-3"
              >
                {/* Employee Info */}
                <div className="border-b pb-3">
                  <h3 className="text-lg text-gray-500">
                    <UserProfileHoverCard employeeId={employee.id}>
                      {employee.fullName}
                    </UserProfileHoverCard>
                  </h3>
                  <p className="text-sm text-gray-500">
                    {displayEmployeeDimensionLabel(
                      employee.jobTitle,
                      isSnapshot,
                      t,
                    )}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {displayEmployeeDimensionLabel(
                        employee.level,
                        isSnapshot,
                        t,
                      )}
                    </span>
                    <span className="text-sm text-gray-500">
                      {employee.location.join(" > ")}
                    </span>
                  </div>
                </div>

                {/* Scores */}
                <div className="grid grid-cols-2 gap-3">
                  {employee.scores.map((score) => (
                    <div
                      key={score.id}
                      className="bg-gray-50 rounded p-2 text-center"
                    >
                      <div className="text-xs text-gray-500 mb-1">
                        {score.label}
                      </div>
                      <div
                        className="text-sm font-medium text-gray-900"
                        style={{
                          color: score.hexColour ? score.hexColour : undefined,
                        }}
                      >
                        {score.value === null ? "-" : score.value}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {totalItemCount > pageSize && (
            <div className="flex flex-row mb-4 mt-4">
              <div>
                <Pagination
                  activePage={pageSearchDetails.pageNumber}
                  itemsCountPerPage={pageSize}
                  totalItemsCount={totalItemCount}
                  pageRangeDisplayed={3}
                  onChange={handlePageChange}
                  activeClass="pagination-active-list-item"
                  itemClass="pagination-list-item"
                  itemClassFirst="pagination-first-item"
                  itemClassLast="pagination-last-item"
                />
              </div>
              {isLoading && (
                <div className="pl-4">
                  <TinyLoader
                    colour={userContext.user.client.accentHexColour}
                  />
                  <span className="ml-1 text-gray-500 text-sm">
                    {t("Common.Loading")}...
                  </span>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MultiScoreTableMobile;
